import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router";
import * as Sentry from "@sentry/react";
import { useSnapshot } from "valtio";
import {
  Camera,
  SwitchCamera,
  CircleStop,
  Circle,
  Pause,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
  Upload,
  Flashlight,
  FlashlightOff,
  Smartphone,
  MessageSquare,
  Grid3X3,
  ShieldCheck,
  Users,
} from "lucide-react";
import { ZoomControls, buildVideoConstraints, buildVideoOnlyConstraints } from "@vizimix/video-capture";
import {
  captureStore,
  setEvent,
  setParticipant,
  setStatus,
  setError,
  setCameraFacing,
  setCameraZoom,
  setZoomCapability,
  setUploadProgress,
  setUploadSpeed,
  setUploadMeta,
  setUploadChunks,
  setRecordedBlob,
  setRecordingDuration,
  addTake,
  setSelectedTakeIndex,
  clearTakes,
  restoreParticipant,
  clearPersistedParticipant,
} from "@/lib/capture-store";
import type { CaptureStatus } from "@/lib/capture-store";
import {
  ApiError,
  fetchEvent,
  joinEvent,
  requestUpload,
  confirmUpload,
  createMultipartUpload,
  uploadPart,
  completeMultipartUpload,
} from "@/lib/api";
import { usePauseableRecorder } from "@/hooks/usePauseableRecorder";
import { useGeofence } from "@/hooks/useGeofence";
import { useAudioLevel } from "@/hooks/useAudioLevel";

// ---------------------------------------------------------------------------
// Browser compatibility check (VIZ-761)
// ---------------------------------------------------------------------------

interface BrowserCheck {
  supported: boolean;
  reason?: string;
}

function checkBrowserCompatibility(): BrowserCheck {
  // Must be served over HTTPS (or localhost)
  if (!window.isSecureContext) {
    return { supported: false, reason: "This page must be loaded over HTTPS." };
  }

  // Must have getUserMedia
  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      supported: false,
      reason: "Your browser doesn't support camera access. Please open this link in Chrome or Safari.",
    };
  }

  // Must have MediaRecorder
  if (typeof MediaRecorder === "undefined") {
    return {
      supported: false,
      reason: "Your browser doesn't support video recording. Please open this link in Chrome or Safari.",
    };
  }

  return { supported: true };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// EventPage
// ---------------------------------------------------------------------------

export function EventPage() {
  const { slug } = useParams<{ slug: string }>();
  const snap = useSnapshot(captureStore);
  const [loading, setLoading] = useState(true);
  const [eventNotFound, setEventNotFound] = useState(false);
  const [eventNotStarted, setEventNotStarted] = useState<{
    title?: string;
    startsAt?: number;
    organizerName?: string;
  } | null>(null);
  const [eventEnded, setEventEnded] = useState<{ title?: string } | null>(null);

  // VIZ-762: Set device context on Sentry so all errors include browser/device info
  useEffect(() => {
    Sentry.setContext("device", {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      deviceMemory: (navigator as unknown as Record<string, unknown>).deviceMemory,
      hardwareConcurrency: navigator.hardwareConcurrency,
      online: navigator.onLine,
    });
  }, []);

  // Load event on mount
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    (async () => {
      try {
        const ev = await fetchEvent(slug);
        if (cancelled) return;
        setEvent(ev);

        // VIZ-1089: Restore participant identity if saved from a previous visit
        if (captureStore.status === "landing" && restoreParticipant(ev.id)) {
          console.log("[EventPage] Restored participant from sessionStorage");
          const consentKey = `vizimix-consent-${ev.id}`;
          if (sessionStorage.getItem(consentKey)) {
            setStatus(ev.geoRequired ? "checking-geo" : "camera-ready");
          } else {
            setStatus("consent");
          }
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.code === "EVENT_NOT_STARTED") {
            setEventNotStarted({
              title: err.data?.title as string | undefined,
              startsAt: err.data?.startsAt as number | undefined,
              organizerName: err.data?.organizerName as string | undefined,
            });
          } else if (err.code === "EVENT_ENDED") {
            setEventEnded({ title: err.data?.title as string | undefined });
          } else {
            setEventNotFound(true);
          }
        } else {
          setEventNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-vizimix-400" />
      </div>
    );
  }

  if (eventNotStarted) {
    const startDate = eventNotStarted.startsAt
      ? new Date(eventNotStarted.startsAt * 1000)
      : null;
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
        <Clock className="h-12 w-12 text-yellow-400" />
        <h1 className="text-xl font-bold">
          {eventNotStarted.title ?? "Event Not Started"}
        </h1>
        <p className="text-vizimix-300">
          This event hasn&apos;t started yet.
          {startDate && (
            <>
              {" "}It&apos;s scheduled for{" "}
              <span className="font-medium text-vizimix-100">
                {startDate.toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}{" "}
                at{" "}
                {startDate.toLocaleTimeString(undefined, {
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
              .
            </>
          )}
        </p>
        {eventNotStarted.organizerName && (
          <p className="text-sm text-vizimix-400">
            Hosted by {eventNotStarted.organizerName}
          </p>
        )}
        <p className="mt-2 text-sm text-vizimix-400">
          Check back when the event begins.
        </p>
      </div>
    );
  }

  if (eventEnded) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
        <AlertCircle className="h-12 w-12 text-vizimix-400" />
        <h1 className="text-xl font-bold">
          {eventEnded.title ?? "Event Ended"}
        </h1>
        <p className="text-vizimix-300">This event has ended.</p>
      </div>
    );
  }

  if (eventNotFound || !snap.event) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-400" />
        <h1 className="text-xl font-bold">Event Not Found</h1>
        <p className="text-vizimix-300">
          This event does not exist or is no longer active.
        </p>
      </div>
    );
  }

  // VIZ-761: Check browser compatibility before showing main content
  const browserCheck = checkBrowserCompatibility();
  if (!browserCheck.supported) {
    const isAndroid = /android/i.test(navigator.userAgent);
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <Smartphone className="h-12 w-12 text-yellow-400" />
        <h1 className="text-xl font-bold">Browser Not Supported</h1>
        <p className="text-vizimix-300 max-w-xs">
          {browserCheck.reason}
        </p>
        {isAndroid && (
          <a
            href={window.location.href}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-2 rounded-full bg-vizimix-500 px-6 py-3 font-medium text-white"
            onClick={(e) => {
              e.preventDefault();
              // Android intent to open in Chrome
              window.location.href = `intent://${window.location.host}${window.location.pathname}#Intent;scheme=https;package=com.android.chrome;end`;
            }}
          >
            Open in Chrome
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="no-scrollbar flex min-h-dvh flex-col">
      <StatusRouter status={snap.status as CaptureStatus} slug={slug!} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status Router -- renders the right view for each status
// ---------------------------------------------------------------------------

function StatusRouter({
  status,
  slug,
}: {
  status: CaptureStatus;
  slug: string;
}) {
  switch (status) {
    case "landing":
      return <LandingView slug={slug} />;
    case "consent":
      return <ConsentView slug={slug} />;
    case "checking-geo":
      return <GeoCheckView slug={slug} />;
    case "camera-ready":
    case "countdown":
    case "recording":
    case "paused":
      // Single component manages camera + recorder lifecycle without remounting
      return <CameraView slug={slug} />;
    case "preview":
      return <PreviewView slug={slug} />;
    case "uploading":
      return <UploadingView />;
    case "complete":
      return <CompleteView slug={slug} />;
    // VIZ-775: "already-submitted" status removed — duplicate emails now allowed
    case "error":
      return <ErrorView slug={slug} />;
  }
}

// ---------------------------------------------------------------------------
// Landing View
// ---------------------------------------------------------------------------

function LandingView({ slug }: { slug: string }) {
  const snap = useSnapshot(captureStore);
  const event = snap.event!;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleJoin = async () => {
    // Validate email is provided and has basic format
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError("Email is required");
      return;
    }
    if (!trimmedEmail.includes("@")) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setEmailError(null);
    setJoining(true);
    setJoinError(null);
    try {
      const result = await joinEvent(
        slug,
        name.trim() || undefined,
        trimmedEmail,
      );
      setParticipant(
        {
          id: result.participantId,
          displayName: result.displayName,
          email: result.email,
          tokenHash: "",
        },
        result.token,
      );

      // Check if already consented this session
      const consentKey = `vizimix-consent-${event.id}`;
      if (sessionStorage.getItem(consentKey)) {
        if (event.geoRequired) {
          setStatus("checking-geo");
        } else {
          setStatus("camera-ready");
        }
      } else {
        setStatus("consent");
      }
    } catch (err) {
      setJoinError(
        err instanceof Error
          ? err.message
          : "Failed to join. Please try again.",
      );
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        {/* Event info */}
        <div className="text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-vizimix-600/20 px-4 py-1.5 text-sm text-vizimix-300">
            <Camera className="h-4 w-4" />
            <span>Live Event</span>
          </div>
          <h1 className="mb-2 text-3xl font-bold text-vizimix-100">
            {event.title}
          </h1>
          {event.description && (
            <p className="mb-2 text-vizimix-300">{event.description}</p>
          )}
          {event.organizerName && (
            <p className="text-sm text-vizimix-400">
              Hosted by {event.organizerName}
            </p>
          )}
        </div>

        {/* Limits info */}
        <div className="flex justify-center gap-6 text-sm text-vizimix-400">
          <span>Max {event.maxRecordingSeconds}s</span>
          <span>
            {event.participantCount}/{event.maxParticipants} joined
          </span>
        </div>

        {/* Name and email fields */}
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-xl border border-capture-border bg-capture-card px-4 py-3 text-vizimix-100 placeholder-vizimix-500 outline-none focus:border-vizimix-500"
          />
          <div>
            <input
              type="email"
              placeholder="Email *"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
              }}
              className={`w-full rounded-xl border bg-capture-card px-4 py-3 text-vizimix-100 placeholder-vizimix-500 outline-none focus:border-vizimix-500 ${emailError ? "border-red-500" : "border-capture-border"}`}
            />
            {emailError && (
              <p className="mt-1 text-sm text-red-400">{emailError}</p>
            )}
          </div>
        </div>

        {/* Join button */}
        <button
          onClick={handleJoin}
          disabled={joining}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-vizimix-600 px-6 py-4 text-lg font-semibold text-white transition-colors hover:bg-vizimix-500 disabled:opacity-50"
        >
          {joining ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Joining...
            </>
          ) : (
            <>
              <Camera className="h-5 w-5" />
              Join &amp; Record
            </>
          )}
        </button>

        {joinError && (
          <p className="text-center text-sm text-red-400">{joinError}</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Consent View — content rights agreement
// ---------------------------------------------------------------------------

function ConsentView({ slug: _slug }: { slug: string }) {
  void _slug;
  const snap = useSnapshot(captureStore);
  const event = snap.event!;
  const [requesting, setRequesting] = useState(false);
  const [permError, setPermError] = useState<string | null>(null);

  const handleAgree = async () => {
    setRequesting(true);
    setPermError(null);

    // VIZ-769: Request camera+mic permission immediately on consent.
    // The stream is stopped right away — CameraView will start its own.
    try {
      const testStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      testStream.getTracks().forEach((t) => t.stop());
    } catch (err) {
      // If only mic was denied, still allow (CameraView handles video-only fallback)
      if (err instanceof DOMException && err.name === "NotAllowedError") {
        try {
          const videoOnly = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
          videoOnly.getTracks().forEach((t) => t.stop());
        } catch {
          setRequesting(false);
          setPermError(
            "Camera access is required to record. Please allow camera access in your browser settings and try again.",
          );
          return;
        }
      } else {
        setRequesting(false);
        setPermError(
          "Camera access is required to record. Please allow camera access in your browser settings and try again.",
        );
        return;
      }
    }

    sessionStorage.setItem(`vizimix-consent-${event.id}`, "1");
    if (event.geoRequired) {
      setStatus("checking-geo");
    } else {
      setStatus("camera-ready");
    }
  };

  const handleCancel = () => {
    setStatus("landing");
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-vizimix-600/20">
          <ShieldCheck className="h-8 w-8 text-vizimix-400" />
        </div>
        <h2 className="text-2xl font-bold text-vizimix-100">
          Content Rights &amp; Camera Access
        </h2>
        <p className="text-vizimix-300 leading-relaxed">
          By tapping &ldquo;I Agree,&rdquo; you grant the event organizer
          permission to use your clip in the final video mix for{" "}
          <span className="font-semibold text-vizimix-200">
            {event.title}
          </span>{" "}
          and consent to camera and microphone access for recording.
        </p>
        <p className="text-sm text-vizimix-400">
          Your clip may be edited, combined with other participants&apos; clips,
          and published by the organizer.
        </p>
        {permError && (
          <p className="text-sm text-red-400">{permError}</p>
        )}
        <div className="flex flex-col gap-3 pt-2">
          <button
            onClick={handleAgree}
            disabled={requesting}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-vizimix-600 px-6 py-4 text-lg font-semibold text-white transition-colors hover:bg-vizimix-500 disabled:opacity-50"
          >
            {requesting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Enabling Camera...
              </>
            ) : (
              "I Agree"
            )}
          </button>
          <button
            onClick={handleCancel}
            disabled={requesting}
            className="w-full rounded-xl border border-capture-border px-6 py-3 text-vizimix-300 hover:bg-capture-card disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Geo Check View
// ---------------------------------------------------------------------------

function GeoCheckView({ slug }: { slug: string }) {
  const snap = useSnapshot(captureStore);
  const geofence = useGeofence();

  useEffect(() => {
    if (!snap.participantToken) return;

    (async () => {
      const allowed = await geofence.checkLocation(
        slug,
        snap.participantToken!,
      );
      if (allowed) {
        captureStore.geoVerified = true;
        setStatus("camera-ready");
      }
    })();
    // Run once on mount
  }, []);

  const handleRetry = async () => {
    if (!snap.participantToken) return;
    const allowed = await geofence.checkLocation(slug, snap.participantToken);
    if (allowed) {
      captureStore.geoVerified = true;
      setStatus("camera-ready");
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
      {geofence.checking ? (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-vizimix-400" />
          <h2 className="text-xl font-semibold">Checking your location...</h2>
          <p className="text-vizimix-300">
            Please allow location access when prompted.
          </p>
        </>
      ) : geofence.error ? (
        <>
          <AlertCircle className="h-12 w-12 text-red-400" />
          <h2 className="text-xl font-semibold">Location Check Failed</h2>
          <p className="text-sm text-vizimix-300">{geofence.error}</p>
          <button
            onClick={handleRetry}
            className="mt-4 rounded-xl bg-vizimix-600 px-6 py-3 font-semibold text-white hover:bg-vizimix-500"
          >
            Try Again
          </button>
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Camera View — unified component for camera-ready, recording, and paused
//
// Keeping all three phases in a single component avoids unmounting the
// MediaRecorder when transitioning from "camera-ready" to "recording".
// ---------------------------------------------------------------------------

const MAX_TAKES = 3;

function CameraView({ slug }: { slug: string }) {
  const snap = useSnapshot(captureStore);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [noAudioWarning, setNoAudioWarning] = useState(false);
  const [countdownValue, setCountdownValue] = useState<number | null>(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [orientationNudge, setOrientationNudge] = useState<"portrait" | "landscape" | null>(null);
  const [gridVisible, setGridVisible] = useState(false);
  const maxDuration = snap.event?.maxRecordingSeconds ?? 120;
  const maxFileMb = snap.event?.maxFileSizeMb ?? 500;
  // Calculate target bitrate: 80% of max to leave headroom
  const targetBitrate = Math.floor((maxFileMb * 8 * 1024 * 1024) / maxDuration * 0.8);
  const recorder = usePauseableRecorder(targetBitrate);
  const stoppingRef = useRef(false);
  // VIZ-343: Track prior takes' cumulative duration to subtract when storing individual take
  const priorDurationRef = useRef(0);

  const isRecording = snap.status === "recording";
  const isPaused = snap.status === "paused";
  const isCountdown = snap.status === "countdown";

  // Audio level meter
  const { level: audioLevel } = useAudioLevel(
    streamRef.current,
    isRecording || isCountdown,
  );

  // Live participant count polling (every 15s)
  const [liveParticipantCount, setLiveParticipantCount] = useState(
    snap.event?.participantCount ?? 0,
  );
  useEffect(() => {
    if (!snap.event?.slug) return;
    const slug = snap.event.slug;
    let cancelled = false;

    const poll = async () => {
      try {
        const ev = await fetchEvent(slug);
        if (!cancelled) setLiveParticipantCount(ev.participantCount);
      } catch {
        // Ignore polling errors
      }
    };

    const interval = setInterval(poll, 15_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [snap.event?.slug]);

  // Orientation detection
  useEffect(() => {
    const mql = window.matchMedia("(orientation: portrait)");
    const checkOrientation = () => {
      // For now, all output targets are portrait-first (tiktok, instagram, youtube_short)
      // So we nudge landscape users to switch
      if (!mql.matches && !isRecording) {
        setOrientationNudge("portrait");
      } else {
        setOrientationNudge(null);
      }
    };
    checkOrientation();
    mql.addEventListener("change", checkOrientation);
    return () => mql.removeEventListener("change", checkOrientation);
  }, [isRecording]);

  // -----------------------------------------------------------------------
  // Camera management
  // -----------------------------------------------------------------------

  const startCamera = useCallback(
    async (facing: "user" | "environment") => {
      setCameraError(null);
      setCameraReady(false);

      // VIZ-650: Preserve existing audio track when switching cameras on iOS.
      // Stopping the audio track and re-requesting it triggers a new permission
      // prompt on Safari. Instead, only stop/replace the video track.
      const existingAudioTrack =
        streamRef.current?.getAudioTracks()[0] ?? null;
      const hasExistingStream = !!streamRef.current;

      // Stop only the video track (keep audio alive for camera switch)
      if (streamRef.current && recorder.status === "inactive") {
        streamRef.current.getVideoTracks().forEach((t) => t.stop());
        // Only stop audio if we don't have a working audio track
        if (!existingAudioTrack || existingAudioTrack.readyState !== "live") {
          streamRef.current.getAudioTracks().forEach((t) => t.stop());
        }
      }

      // If we already have a live audio track, only request video
      const canReuseAudio =
        hasExistingStream &&
        existingAudioTrack &&
        existingAudioTrack.readyState === "live";

      // VIZ-699/VIZ-730: Use shared constraint builder — handles iOS-specific
      // aspectRatio/resizeMode to avoid asymmetric cropping on iPhone.
      const videoConstraints = buildVideoConstraints(facing);

      let stream: MediaStream | null = null;
      let lastError: unknown;
      let audioFallback = false;

      if (canReuseAudio) {
        // VIZ-650: Reuse existing audio — only request new video track
        for (const videoConst of videoConstraints) {
          try {
            const videoStream = await navigator.mediaDevices.getUserMedia({
              video: videoConst,
              audio: false,
            });
            // Combine existing audio with new video
            stream = new MediaStream([
              videoStream.getVideoTracks()[0],
              existingAudioTrack,
            ]);
            break;
          } catch (err) {
            lastError = err;
            if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
              break;
            }
          }
        }
      }

      // Full request (first camera start, or audio reuse failed)
      if (!stream) {
        const attempts: MediaStreamConstraints[] = videoConstraints.map(
          (v) => ({ video: v, audio: true }),
        );

        for (const constraints of attempts) {
          try {
            stream = await navigator.mediaDevices.getUserMedia(constraints);
            break;
          } catch (err) {
            lastError = err;
            if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
              break;
            }
          }
        }

        // If audio+video failed with a permission error, try video-only.
        // This handles Android devices where camera is allowed but mic is denied.
        if (!stream && lastError instanceof DOMException && lastError.name === "NotAllowedError") {
          const videoOnlyAttempts = buildVideoOnlyConstraints(facing);
          for (const constraints of videoOnlyAttempts) {
            try {
              stream = await navigator.mediaDevices.getUserMedia(constraints);
              audioFallback = true;
              console.info("[Capture] Camera started without microphone (mic permission denied)");
              break;
            } catch (err) {
              lastError = err;
              if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
                break;
              }
            }
          }
        }
      }

      if (stream) {
        streamRef.current = stream;
        if (videoRef.current) {
          // VIZ-689: Safari iOS requires clearing srcObject before assigning a new stream,
          // otherwise repeated camera switches show a black preview.
          if (videoRef.current.srcObject && videoRef.current.srcObject !== stream) {
            videoRef.current.srcObject = null;
          }
          videoRef.current.srcObject = stream;
          // VIZ-689: Safari iOS doesn't reliably auto-play after srcObject change —
          // explicitly call play() to prevent black screen on repeated camera switches.
          videoRef.current.play().catch(() => {});
        }

        // Detect zoom capability
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const capabilities = videoTrack.getCapabilities() as MediaTrackCapabilities & {
            zoom?: { min: number; max: number; step: number };
          };
          if (capabilities.zoom) {
            setZoomCapability({
              min: capabilities.zoom.min,
              max: capabilities.zoom.max,
              step: capabilities.zoom.step,
            });
          } else {
            setZoomCapability(null);
          }

          // Detect torch support
          const hasTorch = !!(capabilities as Record<string, unknown>).torch;
          setTorchSupported(hasTorch);
          setTorchOn(false);
        }
        setCameraZoom(1);
        setNoAudioWarning(audioFallback);
        setCameraReady(true);
      } else {
        const errName = lastError instanceof DOMException ? lastError.name : "";
        console.warn("[Capture] Camera access failed:", errName, lastError);

        // VIZ-762: Report camera failures to Sentry with device context
        Sentry.captureException(lastError instanceof Error ? lastError : new Error(`Camera init failed: ${errName || "unknown"}`), {
          tags: { stage: "camera-init", errorName: errName || "unknown" },
          extra: { facing, canReuseAudio, audioFallback },
        });

        const message =
          errName === "NotAllowedError"
            ? "Camera access was denied. Please allow camera and microphone permissions in your browser settings, then tap Retry."
            : errName === "NotFoundError"
              ? "No camera found on this device. Please make sure your device has a camera."
              : errName === "NotReadableError"
                ? "Camera is in use by another app. Please close other apps using the camera and tap Retry."
                : "Unable to access camera. Please check your permissions and try again.";
        setCameraError(message);
      }
    },
    [recorder.status],
  );

  // Throttle applyConstraints to avoid stuttering and brief pauses on mobile.
  // UI state updates immediately; the hardware zoom catches up at ~60ms intervals.
  const zoomTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingZoomRef = useRef<number | null>(null);

  const applyZoom = useCallback((level: number) => {
    // Guard against NaN/Infinity corrupting zoom state
    if (!Number.isFinite(level)) return;

    // Update UI immediately for responsive slider
    setCameraZoom(level);

    // Throttle the actual hardware constraint application
    pendingZoomRef.current = level;
    if (zoomTimerRef.current) return; // already scheduled

    zoomTimerRef.current = setTimeout(() => {
      zoomTimerRef.current = null;
      const pending = pendingZoomRef.current;
      if (pending == null || !streamRef.current) return;
      pendingZoomRef.current = null;

      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (!videoTrack) return;
      videoTrack.applyConstraints({
        // @ts-expect-error -- zoom is not in the standard TS types yet
        advanced: [{ zoom: pending }],
      }).catch(() => {
        // Device doesn't support zoom — ignore
      });
    }, 60);
  }, []);

  useEffect(() => {
    startCamera(captureStore.cameraFacing);

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (zoomTimerRef.current) {
        clearTimeout(zoomTimerRef.current);
      }
    };
  }, []);

  const handleFlip = () => {
    if (isRecording || isPaused) return; // Don't flip while recording or paused
    const newFacing =
      snap.cameraFacing === "user" ? "environment" : "user";
    setCameraFacing(newFacing);
    startCamera(newFacing);
  };

  const handleToggleTorch = () => {
    if (!streamRef.current || !torchSupported) return;
    const videoTrack = streamRef.current.getVideoTracks()[0];
    if (!videoTrack) return;
    const newState = !torchOn;
    videoTrack.applyConstraints({
      // @ts-expect-error -- torch is not in the standard TS types yet
      advanced: [{ torch: newState }],
    }).then(() => {
      setTorchOn(newState);
    }).catch(() => {
      // Device doesn't support torch — ignore
    });
  };

  // -----------------------------------------------------------------------
  // Recording controls
  // -----------------------------------------------------------------------

  const handleStartRecording = () => {
    if (!streamRef.current) return;

    // Start countdown
    setStatus("countdown");
    setCountdownValue(3);

    let count = 3;
    const interval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdownValue(count);
      } else {
        clearInterval(interval);
        setCountdownValue(null);
        // Actually start recording
        if (streamRef.current) {
          // VIZ-343: Pass cumulative duration from previous takes so timer continues
          const priorDuration = snap.takes.reduce((sum, t) => sum + t.duration, 0);
          priorDurationRef.current = priorDuration;
          recorder.start(streamRef.current, priorDuration);
          setStatus("recording");
          navigator.vibrate?.(50);
        }
      }
    }, 1000);
  };

  // VIZ-664: Pause/resume support — keeps camera stream alive
  const handlePause = useCallback(() => {
    recorder.pause();
    setStatus("paused");
    navigator.vibrate?.(30);
  }, [recorder]);

  const handleResume = useCallback(() => {
    recorder.resume();
    setStatus("recording");
    navigator.vibrate?.(30);
  }, [recorder]);

  const handleStop = useCallback(async () => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;

    try {
      const blob = await recorder.stop();
      setRecordedBlob(blob);
      // VIZ-343: Store only this take's duration, not cumulative
      addTake(blob, recorder.activeDuration - priorDurationRef.current);

      // Stop the current media tracks — no longer needed after stop
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      // Double-pulse haptic on stop
      navigator.vibrate?.([50, 50, 50]);

      // VIZ-726: Stop is final — proceed directly to upload, no option to record again.
      // VIZ-774: Confirm before uploading
      if (!window.confirm("Are you done recording?")) return;
      // Upload all accumulated takes immediately.
      setStatus("uploading");
      const allTakes = [...captureStore.takes];
      for (const take of allTakes) {
        setRecordedBlob(take.blob as Blob);
        setRecordingDuration(take.duration);
        await uploadBlob(slug, take.blob as Blob);
        if (captureStore.status === "error") return;
      }
      // VIZ-1089: Clear persisted state after successful upload
      if (captureStore.event?.id) {
        clearPersistedParticipant(captureStore.event.id);
      }
      setStatus("complete");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Recording failed. Please try again.",
      );
    } finally {
      stoppingRef.current = false;
    }
  }, [recorder, slug]);

  // Sync active duration to the store
  useEffect(() => {
    setRecordingDuration(recorder.activeDuration);
  }, [recorder.activeDuration]);

  // Auto-stop at max duration
  useEffect(() => {
    if (recorder.activeDuration >= maxDuration && recorder.status !== "inactive") {
      handleStop();
    }
  }, [recorder.activeDuration, maxDuration, handleStop, recorder.status]);

  // VIZ-771: Release camera/mic when user switches apps (visibilitychange)
  // Saves any in-progress recording as a take so no data is lost.
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // If actively recording or paused, stop the recorder to save the take
        if (recorder.status === "recording" || recorder.status === "paused") {
          try {
            const blob = await recorder.stop();
            addTake(blob, recorder.activeDuration - priorDurationRef.current);
            setRecordedBlob(blob);
          } catch {
            // Recorder may already be inactive — ignore
          }
          setStatus("camera-ready");
        }
        // Release all media tracks so other apps can use camera/mic
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
      } else {
        // Re-acquire camera when returning to the app
        if (!streamRef.current && captureStore.status !== "uploading" && captureStore.status !== "complete" && captureStore.status !== "error") {
          startCamera(captureStore.cameraFacing);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [recorder, startCamera]);

  // -----------------------------------------------------------------------
  // Derived
  // -----------------------------------------------------------------------

  const progress = isRecording || isPaused
    ? Math.min((recorder.activeDuration / maxDuration) * 100, 100)
    : 0;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="relative flex h-dvh flex-col bg-black">
      {/* Camera feed */}
      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="h-full w-full object-cover"
        />

        {/* Camera loading overlay — VIZ-686 */}
        {!cameraReady && !cameraError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="text-center">
              <Loader2 className="mx-auto mb-3 h-10 w-10 text-white animate-spin" />
              <p className="text-vizimix-200 text-sm">Enabling camera...</p>
            </div>
          </div>
        )}

        {/* Camera error overlay */}
        {cameraError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-6">
            <div className="text-center">
              <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-400" />
              <p className="text-vizimix-200">{cameraError}</p>
              <button
                onClick={() => startCamera(captureStore.cameraFacing)}
                className="mt-4 rounded-xl bg-vizimix-600 px-6 py-3 font-semibold text-white"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* No-audio warning banner */}
        {noAudioWarning && !cameraError && (
          <div className="absolute top-2 left-2 right-2 z-10 rounded-lg bg-amber-600/90 px-3 py-2 text-center text-sm text-white backdrop-blur-sm">
            Recording without audio — microphone permission was denied
          </div>
        )}

        {/* Countdown overlay */}
        {isCountdown && countdownValue && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <span
              key={countdownValue}
              className="text-8xl font-bold text-white drop-shadow-2xl animate-[ping_0.8s_ease-out_1]"
            >
              {countdownValue}
            </span>
          </div>
        )}

        {/* Rule-of-thirds grid overlay */}
        {gridVisible && (
          <div className="absolute inset-0 pointer-events-none">
            {/* Vertical lines at 1/3 and 2/3 */}
            <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
            <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
            {/* Horizontal lines at 1/3 and 2/3 */}
            <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
            <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
          </div>
        )}

        {/* Zoom slider — shared component from @vizimix/video-capture (smooth continuous zoom) */}
        {snap.zoomCapability && (
          <ZoomControls
            zoomCapability={snap.zoomCapability}
            currentZoom={snap.cameraZoom}
            onZoomChange={applyZoom}
          />
        )}

        {/* Audio level meter (left edge) */}
        {(isRecording || isCountdown) && (
          <div className="absolute left-4 top-20 bottom-28 flex flex-col justify-end">
            <div className="h-32 w-2 rounded-full bg-white/20 overflow-hidden rotate-180">
              <div
                className="w-full rounded-full transition-all duration-100"
                style={{
                  height: `${Math.min(audioLevel * 100, 100)}%`,
                  backgroundColor:
                    audioLevel > 0.8
                      ? "#ef4444" // red — clipping
                      : audioLevel > 0.5
                        ? "#eab308" // yellow — loud
                        : "#22c55e", // green — good
                }}
              />
            </div>
            <span className="mt-1 text-[10px] text-white/60 text-center">MIC</span>
          </div>
        )}

        {/* Orientation nudge */}
        {orientationNudge && !isRecording && !isPaused && !isCountdown && (
          <div className="absolute top-20 left-0 right-0 flex justify-center px-4 animate-fade-in">
            <div className="flex items-center gap-2 rounded-full bg-amber-500/90 px-4 py-2 text-sm font-medium text-black backdrop-blur-sm">
              <Smartphone className="h-4 w-4" />
              Rotate to portrait for best results
            </div>
          </div>
        )}

        {/* Recording prompt overlay */}
        {snap.event?.recordingPrompt && !isCountdown && (
          <div className="absolute bottom-28 left-0 right-0 flex justify-center px-4">
            <div className="flex items-center gap-2 max-w-sm rounded-xl bg-black/60 px-4 py-3 backdrop-blur-sm">
              <MessageSquare className="h-4 w-4 shrink-0 text-vizimix-300" />
              <p className="text-sm text-white/90">{snap.event.recordingPrompt}</p>
            </div>
          </div>
        )}

        {/* Top bar */}
        <div className="absolute left-0 right-0 top-0 p-4">
          {isRecording || isPaused ? (
            <div className="mx-auto w-fit rounded-2xl bg-black/50 px-6 py-3 backdrop-blur-sm">
              {/* Recording/paused indicator + timer */}
              <div className="flex items-center justify-center gap-3">
                <span className={`h-3 w-3 rounded-full bg-red-500 ${isRecording ? "animate-pulse" : ""}`} />
                <span className="font-mono text-2xl font-bold text-white drop-shadow-lg">
                  {formatTimer(recorder.activeDuration)}
                </span>
                {isPaused && (
                  <span className="text-xs font-medium text-yellow-400">PAUSED</span>
                )}
              </div>
              {/* Progress bar */}
              <div className="mx-auto mt-3 h-1 max-w-xs overflow-hidden rounded-full bg-white/20">
                <div
                  className="h-full rounded-full bg-red-500 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="mt-1 text-center text-xs text-white/80">
                {recorder.activeDuration}/{maxDuration}s
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-black/50 px-3 py-1.5 text-sm text-white backdrop-blur-sm">
                  Max {maxDuration}s
                </div>
                <div className="flex items-center gap-1.5 rounded-full bg-black/50 px-3 py-1.5 text-sm text-white backdrop-blur-sm">
                  <Users className="h-3.5 w-3.5" />
                  {liveParticipantCount} here
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setGridVisible((v) => !v)}
                  className={`rounded-full p-3 backdrop-blur-sm active:bg-black/70 ${
                    gridVisible ? "bg-white/30 text-white" : "bg-black/50 text-white"
                  }`}
                  aria-label={gridVisible ? "Hide grid" : "Show grid"}
                >
                  <Grid3X3 className="h-6 w-6" />
                </button>
                {torchSupported && (
                  <button
                    onClick={handleToggleTorch}
                    className={`rounded-full p-3 backdrop-blur-sm active:bg-black/70 ${
                      torchOn ? "bg-yellow-500/70 text-white" : "bg-black/50 text-white"
                    }`}
                    aria-label={torchOn ? "Turn off flashlight" : "Turn on flashlight"}
                  >
                    {torchOn ? (
                      <FlashlightOff className="h-6 w-6" />
                    ) : (
                      <Flashlight className="h-6 w-6" />
                    )}
                  </button>
                )}
                <button
                  onClick={handleFlip}
                  className="rounded-full bg-black/50 p-3 text-white backdrop-blur-sm active:bg-black/70"
                >
                  <SwitchCamera className="h-6 w-6" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom controls */}
      <div className="safe-area-bottom bg-black/90 p-6 backdrop-blur-sm">
        {isCountdown ? (
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-vizimix-300">Get ready...</p>
          </div>
        ) : isRecording || isPaused ? (
          /* VIZ-664: Recording controls — pause/resume + stop */
          <div className="flex items-center justify-center gap-8">
            {/* Pause / Resume toggle */}
            <button
              onClick={isRecording ? handlePause : handleResume}
              className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-red-500 transition-transform active:scale-95"
              aria-label={isRecording ? "Pause recording" : "Resume recording"}
            >
              {isRecording ? (
                <Pause className="h-8 w-8 text-white" />
              ) : (
                <Circle className="h-8 w-8 fill-red-500 text-red-500" />
              )}
            </button>
            {/* Stop (finish this clip) */}
            <button
              onClick={handleStop}
              className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/60 bg-black/50 transition-transform active:scale-95"
              aria-label="Stop recording"
            >
              <CircleStop className="h-6 w-6 text-white" />
            </button>
          </div>
        ) : (
          /* Camera-ready controls — tap to start recording */
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-vizimix-300">
              {snap.event!.title}
            </p>
            <button
              onClick={handleStartRecording}
              disabled={!!cameraError || !cameraReady}
              className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-red-500 transition-transform active:scale-95 disabled:opacity-50"
            >
              <div className="h-7 w-7 rounded-full bg-white" />
            </button>
            <p className="text-xs text-vizimix-400">
              Tap to start recording
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview View — review recording before uploading
// ---------------------------------------------------------------------------

function PreviewView({ slug }: { slug: string }) {
  const snap = useSnapshot(captureStore);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const currentTake = snap.takes[snap.selectedTakeIndex];
  const takeCount = snap.takes.length;

  useEffect(() => {
    if (currentTake) {
      const url = URL.createObjectURL(currentTake.blob as Blob);
      setBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [currentTake]);

  const handleUpload = async () => {
    if (snap.takes.length === 0) return;
    // VIZ-774: Confirm before uploading
    if (!window.confirm("Are you done recording?")) return;
    setStatus("uploading");
    // Upload ALL takes, not just the selected one
    for (const take of snap.takes) {
      setRecordedBlob(take.blob as Blob);
      setRecordingDuration(take.duration);
      await uploadBlob(slug, take.blob as Blob);
      // If an error occurred during upload, stop processing remaining takes
      if (captureStore.status === "error") return;
    }
    setStatus("complete");
  };

  const handleRecordAnother = () => {
    captureStore.recordedBlob = null;
    captureStore.recordingDuration = 0;
    setStatus("camera-ready");
  };

  const handleReRecord = () => {
    clearTakes();
    captureStore.recordedBlob = null;
    captureStore.recordingDuration = 0;
    setStatus("camera-ready");
  };

  return (
    <div className="flex min-h-dvh flex-col bg-black">
      {/* Video preview */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {blobUrl && (
          <video
            ref={videoRef}
            src={blobUrl}
            controls
            playsInline
            className="h-full w-full object-contain"
          />
        )}
      </div>

      {/* Take selector dots */}
      {takeCount > 1 && (
        <div className="flex items-center justify-center gap-3 bg-black/90 pt-4">
          {snap.takes.map((_, i) => (
            <button
              key={i}
              onClick={() => setSelectedTakeIndex(i)}
              className={`flex items-center justify-center rounded-full transition-all ${
                i === snap.selectedTakeIndex
                  ? "h-10 w-10 border-2 border-vizimix-500 bg-vizimix-600 text-white text-sm font-bold"
                  : "h-8 w-8 border border-capture-border bg-capture-card text-vizimix-400 text-xs"
              }`}
              aria-label={`Take ${i + 1}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="safe-area-bottom bg-black/90 p-6 backdrop-blur-sm">
        <p className="mb-4 text-center text-sm text-vizimix-300">
          {takeCount > 1 ? (
            <>Take {snap.selectedTakeIndex + 1} of {takeCount}</>
          ) : (
            <>Review your clip before uploading</>
          )}
          {currentTake && currentTake.duration > 0 && (
            <span className="ml-2 text-vizimix-400">
              ({formatTimer(currentTake.duration)})
            </span>
          )}
        </p>
        <div className="flex flex-col items-center gap-3">
          {/* Upload is the primary action — circular, matching record button shape */}
          <button
            onClick={handleUpload}
            className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-blue-500 transition-transform active:scale-95"
            aria-label={`Upload${takeCount > 1 ? ` all ${takeCount} takes` : ""}`}
          >
            <Upload className="h-8 w-8 text-white" />
          </button>
          {takeCount > 1 && (
            <p className="text-xs text-vizimix-400">Upload All ({takeCount})</p>
          )}
          {/* Secondary action — smaller, less prominent */}
          {takeCount < MAX_TAKES ? (
            <button
              onClick={handleRecordAnother}
              className="flex items-center gap-2 rounded-xl border border-capture-border px-5 py-2.5 text-sm text-vizimix-300 hover:bg-capture-card active:scale-95"
            >
              <Camera className="h-4 w-4" />
              {takeCount === 0 ? "Re-record" : "Record Another"}
            </button>
          ) : (
            <button
              onClick={handleReRecord}
              className="flex items-center gap-2 rounded-xl border border-capture-border px-5 py-2.5 text-sm text-vizimix-300 hover:bg-capture-card active:scale-95"
            >
              <Camera className="h-4 w-4" />
              Start Over
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload logic
// ---------------------------------------------------------------------------

async function uploadBlob(slug: string, blob: Blob) {
  const token = captureStore.participantToken;
  if (!token) {
    setError("Session expired. Please rejoin the event.");
    return;
  }

  try {
    setUploadProgress(0);

    // Determine file extension from blob type
    const ext = blob.type.includes("mp4") ? "mp4" : "webm";
    const filename = `capture-${Date.now()}.${ext}`;

    // 1. Request upload
    const { fileId, uploadUrl, uploadMethod, chunkSize } = await requestUpload(
      slug,
      token,
      filename,
      blob.type,
      blob.size,
    );

    setUploadMeta(blob.size);

    // 2. Upload — chunked multipart or single PUT fallback
    if (uploadMethod === "multipart") {
      await uploadBlobChunked(slug, token, blob, fileId, chunkSize ?? 10 * 1024 * 1024);
    } else {
      await uploadBlobDirect(token, blob, uploadUrl);
    }

    setUploadProgress(100);

    // 3. Confirm upload
    await confirmUpload(slug, token, fileId);
  } catch (err) {
    setError(
      err instanceof Error
        ? err.message
        : "Upload failed. Please try again.",
    );
  }
}

/** Chunked multipart upload with per-chunk retry. */
async function uploadBlobChunked(
  slug: string,
  token: string,
  blob: Blob,
  fileId: string,
  chunkSize: number,
) {
  const MAX_RETRIES = 3;
  const totalChunks = Math.ceil(blob.size / chunkSize);
  const parts: { partNumber: number; etag: string }[] = [];

  setUploadChunks(0, totalChunks);

  // Create multipart upload session
  const { uploadId } = await createMultipartUpload(slug, token, fileId);

  let uploadedBytes = 0;
  let lastSpeedTime = Date.now();
  let lastSpeedBytes = 0;

  for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
    const start = (partNumber - 1) * chunkSize;
    const end = Math.min(start + chunkSize, blob.size);
    const chunk = blob.slice(start, end);

    // Per-chunk retry with exponential backoff
    let lastError: Error | null = null;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          const delay = Math.pow(2, attempt - 1) * 1000;
          await new Promise((r) => setTimeout(r, delay));
        }

        // Use XHR for intra-chunk progress tracking
        const { etag } = await uploadChunkWithProgress(
          slug,
          token,
          fileId,
          uploadId,
          partNumber,
          chunk,
          (chunkLoaded) => {
            const totalLoaded = uploadedBytes + chunkLoaded;
            setUploadProgress(Math.round((totalLoaded / blob.size) * 100));

            // Speed calculation every 500ms
            const now = Date.now();
            const elapsed = (now - lastSpeedTime) / 1000;
            if (elapsed > 0.5) {
              const bytesInInterval = totalLoaded - lastSpeedBytes;
              setUploadSpeed(bytesInInterval / elapsed);
              lastSpeedTime = now;
              lastSpeedBytes = totalLoaded;
            }
          },
        );

        parts.push({ partNumber, etag });
        uploadedBytes += chunk.size;
        setUploadChunks(partNumber, totalChunks);
        lastError = null;
        break;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
      }
    }

    if (lastError) {
      throw new Error(
        `Upload failed on chunk ${partNumber}/${totalChunks} after ${MAX_RETRIES + 1} attempts: ${lastError.message}`,
      );
    }
  }

  // Complete multipart upload
  await completeMultipartUpload(slug, token, fileId, uploadId, parts);
}

/** Upload a single chunk via XHR for progress tracking. */
function uploadChunkWithProgress(
  slug: string,
  token: string,
  fileId: string,
  uploadId: string,
  partNumber: number,
  chunk: Blob,
  onProgress: (loaded: number) => void,
): Promise<{ etag: string }> {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams({
      uploadId,
      partNumber: String(partNumber),
    });
    const url = `/api/event/${encodeURIComponent(slug)}/upload/${fileId}/multipart/part?${params}`;

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(e.loaded);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText) as { etag: string };
          resolve(data);
        } catch {
          reject(new Error("Invalid response from server"));
        }
      } else {
        reject(new Error(`Chunk upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () =>
      reject(new Error("Upload failed. Check your connection."));
    xhr.send(chunk);
  });
}

/** Single PUT upload (legacy fallback). */
async function uploadBlobDirect(token: string, blob: Blob, uploadUrl: string) {
  const MAX_RETRIES = 3;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl, true);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);

        let lastLoaded = 0;
        let lastTime = Date.now();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
            const now = Date.now();
            const elapsed = (now - lastTime) / 1000;
            if (elapsed > 0.5) {
              const bytesInInterval = e.loaded - lastLoaded;
              setUploadSpeed(bytesInInterval / elapsed);
              lastLoaded = e.loaded;
              lastTime = now;
            }
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () =>
          reject(new Error("Upload failed. Check your connection."));
        xhr.send(blob);
      });
      break; // Success — exit retry loop
    } catch (uploadErr) {
      if (attempt < MAX_RETRIES) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attempt) * 1000;
        setUploadProgress(0);
        setUploadSpeed(0);
        await new Promise((r) => setTimeout(r, delay));
      } else {
        throw uploadErr; // Final attempt failed
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Uploading View
// ---------------------------------------------------------------------------

function formatSpeed(bytesPerSecond: number): string {
  if (bytesPerSecond <= 0) return "";
  if (bytesPerSecond > 1024 * 1024) return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
  return `${(bytesPerSecond / 1024).toFixed(0)} KB/s`;
}

function formatEta(seconds: number): string {
  if (seconds <= 0 || !isFinite(seconds)) return "";
  if (seconds < 60) return `${Math.ceil(seconds)}s remaining`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.ceil(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")} remaining`;
}

function UploadingView() {
  const snap = useSnapshot(captureStore);

  const remainingBytes = snap.uploadTotalBytes * (1 - snap.uploadProgress / 100);
  const eta = snap.uploadBytesPerSecond > 0 ? remainingBytes / snap.uploadBytesPerSecond : 0;

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 p-6">
      <Upload className="h-12 w-12 text-vizimix-400" />
      <h2 className="text-xl font-semibold">Uploading your clip...</h2>

      <div className="w-full max-w-xs">
        <div className="h-2 overflow-hidden rounded-full bg-capture-card">
          <div
            className="h-full rounded-full bg-vizimix-500 transition-all duration-300"
            style={{ width: `${snap.uploadProgress}%` }}
          />
        </div>
        <p className="mt-2 text-center text-sm text-vizimix-300">
          {snap.uploadProgress}%
        </p>
        {snap.uploadBytesPerSecond > 0 && (
          <p className="mt-1 text-center text-xs text-vizimix-400">
            {formatSpeed(snap.uploadBytesPerSecond)}
            {eta > 0 && ` — ${formatEta(eta)}`}
          </p>
        )}
        {snap.uploadTotalChunks > 1 && (
          <p className="mt-1 text-center text-xs text-vizimix-400">
            Chunk {snap.uploadChunksCompleted} of {snap.uploadTotalChunks}
          </p>
        )}
      </div>

      <p className="text-sm text-vizimix-400">
        Please keep this page open until the upload completes.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Complete View
// ---------------------------------------------------------------------------

function CompleteView({ slug }: { slug: string }) {
  const snap = useSnapshot(captureStore);
  const [showUploadOption, setShowUploadOption] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILES = 2;
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  // Redirect after dismissing or completing additional upload
  useEffect(() => {
    if (!showUploadOption && !uploading) {
      const timer = setTimeout(() => {
        window.location.href = "https://vizi.video";
      }, uploadComplete ? 3000 : 0);
      return () => clearTimeout(timer);
    }
  }, [showUploadOption, uploading, uploadComplete]);

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    setUploadError(null);

    // Validate count
    if (files.length > MAX_FILES) {
      setUploadError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    // Validate size
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`${file.name} exceeds 100MB limit`);
        return;
      }
      if (!file.type.startsWith("video/")) {
        setUploadError(`${file.name} is not a video file`);
        return;
      }
    }

    setSelectedFiles(files);
  }

  async function handleUploadFiles() {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setUploadError(null);

    try {
      const token = sessionStorage.getItem(`vizimix-participant-token-${snap.event?.id}`) ?? "";

      for (const file of selectedFiles) {
        // Request upload slot
        const upload = await requestUpload(slug, token, file.name, file.type, file.size);

        // Create multipart upload
        const fileId = upload.fileId;
        const { uploadId } = await createMultipartUpload(slug, token, fileId);

        // Upload in chunks (5MB each)
        const CHUNK_SIZE = 5 * 1024 * 1024;
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const parts: Array<{ partNumber: number; etag: string }> = [];

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          const partNumber = i + 1;

          const { etag } = await uploadPart(slug, token, fileId, uploadId, partNumber, chunk);
          parts.push({ partNumber, etag });
        }

        await completeMultipartUpload(slug, token, fileId, uploadId, parts);
      }

      setUploadComplete(true);
      setShowUploadOption(false);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  if (!showUploadOption || uploadComplete) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-6 p-6 text-center">
        <div className="relative">
          <CheckCircle2 className="h-16 w-16 text-green-400" />
          <div className="absolute -inset-4 rounded-full bg-green-400/10 animate-[ping_2s_ease-out_1]" />
        </div>
        <h2 className="text-2xl font-bold">
          {uploadComplete ? "All clips submitted!" : "Your clip has been submitted!"}
        </h2>
        <p className="text-vizimix-300">
          Thanks for recording at{" "}
          <span className="font-semibold text-vizimix-200">
            {snap.event?.title}
          </span>
          . The organizer will use your clips in the final mix.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 p-6 text-center">
      <div className="relative">
        <CheckCircle2 className="h-16 w-16 text-green-400" />
        <div className="absolute -inset-4 rounded-full bg-green-400/10 animate-[ping_2s_ease-out_1]" />
      </div>
      <h2 className="text-2xl font-bold">Your clip has been submitted!</h2>
      <p className="text-vizimix-300">
        Want to add clips from your camera roll?
      </p>
      <p className="text-xs text-vizimix-400">
        Up to {MAX_FILES} video files, 100MB each
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />

      {selectedFiles.length > 0 && (
        <div className="space-y-2 w-full max-w-xs">
          {selectedFiles.map((f, i) => (
            <div key={i} className="text-sm text-vizimix-200 bg-vizimix-800/50 rounded-lg px-3 py-2 flex justify-between">
              <span className="truncate">{f.name}</span>
              <span className="text-vizimix-400 ml-2">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
            </div>
          ))}
        </div>
      )}

      {uploadError && (
        <p className="text-sm text-red-400">{uploadError}</p>
      )}

      <div className="flex gap-3">
        {selectedFiles.length === 0 ? (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="rounded-xl bg-vizimix-600 px-6 py-3 font-semibold text-white hover:bg-vizimix-500"
          >
            Choose Videos
          </button>
        ) : (
          <button
            onClick={handleUploadFiles}
            disabled={uploading}
            className="rounded-xl bg-vizimix-600 px-6 py-3 font-semibold text-white hover:bg-vizimix-500 disabled:opacity-50"
          >
            {uploading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </span>
            ) : (
              `Upload ${selectedFiles.length} clip${selectedFiles.length > 1 ? "s" : ""}`
            )}
          </button>
        )}
        <button
          onClick={() => setShowUploadOption(false)}
          disabled={uploading}
          className="text-sm text-vizimix-400 underline"
        >
          No thanks
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Error View
// ---------------------------------------------------------------------------

function ErrorView({ slug: _slug }: { slug: string }) {
  void _slug;
  const snap = useSnapshot(captureStore);

  const handleRetry = () => {
    captureStore.error = null;
    // Go back to camera-ready if we had joined, otherwise landing
    if (snap.participant) {
      if (snap.event?.geoRequired && !snap.geoVerified) {
        setStatus("checking-geo");
      } else {
        setStatus("camera-ready");
      }
    } else {
      setStatus("landing");
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
      <AlertCircle className="h-12 w-12 text-red-400" />
      <h2 className="text-xl font-semibold">Something went wrong</h2>
      <p className="max-w-sm text-sm text-vizimix-300">
        {snap.error || "An unexpected error occurred."}
      </p>
      <button
        onClick={handleRetry}
        className="mt-4 rounded-xl bg-vizimix-600 px-6 py-3 font-semibold text-white hover:bg-vizimix-500"
      >
        Try Again
      </button>
      <button
        onClick={() => {
          captureStore.error = null;
          setStatus("landing");
        }}
        className="text-sm text-vizimix-400 underline"
      >
        Start Over
      </button>
    </div>
  );
}
